import setIfNotSet from "@irati/utils/setIfNotSet"

import PBService from "@irati/utils/services"
import client, {QueryOptions, MutationOptions} from "@irati/utils/services/apollo"
import {Deal, DealsListType, DealStatus, QueryDealsArgs, CompanyBenefitInput, CompanyBenefitUpdateInput, DealList} from "@irati/types"

import dealService from "./dealsGraph"
import dealsErrorHandler from "./errors/dealsErrorHandler"
import createCompanyBenefitMutation, {createCompanyBenefitsKey} from "./mutations/companyBenefitCreate"
import udpateCompanyBenefitMutation, {updateCompanyBenefitsKey} from "./mutations/companyBenefitUpdate"
import archiveCompanyBenefitMutation from "./mutations/companyBenefitArchive"

class CompanyBenefitService extends PBService {
  list = (variables: Partial<QueryDealsArgs> = {}, options: QueryOptions = {}): Promise<DealList> => {
    variables.type = DealsListType.Company
    variables.status = DealStatus.On
    return dealService.list(variables, options)
  }

  get = (id: string, options: QueryOptions = {}) => {
    return dealService.deal({uuid: id}, options)
  }

  getForAdmin = (id: string, options: QueryOptions = {}) => {
    return this.list({uuids: [id], ignoreBlacklist: true}, options).then(({results}) => {
      return results.filter((t: Deal) => t.uuid === id)[0]
    })
  }

  create = (benefit: Deal, options: MutationOptions = {}): Promise<Deal> => {
    const details = parseForUpdate(benefit)
    setIfNotSet(details, "type", DealsListType.Company)
    const variables = {details}

    return client.mutate({...options, mutation: createCompanyBenefitMutation, variables}).then(({data}) => data[createCompanyBenefitsKey])
  }

  update = (id: string, benefit: Deal, options: MutationOptions = {}) => {
    const details = parseForUpdate(benefit)
    const variables = {id, details}

    return client.mutate({...options, mutation: udpateCompanyBenefitMutation, variables}).then(({data}) => data[updateCompanyBenefitsKey])
  }

  archive = (id: string, options: MutationOptions = {}): Promise<boolean> => {
    const variables = {id}
    return client.mutate({...options, mutation: archiveCompanyBenefitMutation, variables}).then(() => true)
  }
}

const parseForCreate = (benefit: Deal): CompanyBenefitInput => {
  return {
    bannerImage: benefit.bannerImage,
    cardImage: benefit.cardImage,
    contactInfo: benefit.contactInfo,
    description: benefit.description,
    header: benefit.header,
    importantInfo: benefit.importantInfo,
    locale: benefit.locale,
    redemptionInstructions: benefit.redemptionInstructions,
    savings: benefit.savings,
    status: benefit.status,
    teamIds: benefit.teamIds,
    thumbnailImage: benefit.thumbnailImage,
    type: <DealsListType>benefit.type
  }
}

const parseForUpdate = (benefit: Deal): CompanyBenefitUpdateInput => {
  return {
    ...parseForCreate(benefit),
    urlSlug: benefit.urlSlug
  }
}

export default new CompanyBenefitService({errorHandlers: [dealsErrorHandler]})
